import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, esES, GridColDef } from '@mui/x-data-grid';
import { Button, Chip, Grid, LinearProgress,  TextField,  Typography,  useMediaQuery, useTheme } from '@mui/material';
import HeaderFab from '../HeaderFab';
import { GrupoType, useDeleteGrupos, useGetGrupos } from '../../hooks/useGrupos';
import GrupoDetail from './GrupoDetail';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/userContext';
import WarningDialog from '../warningDialog';
import { useGetAlumnos } from '../../hooks/getAlumnos';
import { useAutoPostAlumno } from '../../hooks/usePostAlumno';
import { AlumnoType, PayloadAlumnoType } from '../../types/types';
import { useDebouncedCallback } from 'use-debounce';
import { useQueryClient } from 'react-query';
import AlumnosParaAgregarAlGrupo from './AlumnosParaAgragarAlGrupo';

export default function Grupos() {
  const { user } = useContext(UserContext);

  const grupos = useGetGrupos(user);
  const [grupoAEditar, setGrupoAEditar] = useState<GrupoType | undefined>();
  const [grupoABorrar, setGrupoABorrar] = useState<string>();
  const deleteGrupo = useDeleteGrupos(grupoABorrar, user);
  const [warning, setWarning] = useState(false);
  const [agregarAlumnos, setAgregarAlumnos] = useState<{ alumnos?: AlumnoType[], grupo: string } | null>(null);
  const theme = useTheme();
  const [verDetalleGrupo, setVerDetalleGrupo] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filtro, setFiltro] = useState('');
  const alumnos = useGetAlumnos(user);
  const [ mutableAlumno, setMutableAlumno ] = useState<PayloadAlumnoType | undefined>();
  const editAlumno = useAutoPostAlumno({ alumno: mutableAlumno }, user);

  // the QC is to detect activity and perfom the debunced refetch, editAlumno.xxx doesnt trigger useEffect
  const queryClient = useQueryClient();
  const qc = queryClient.isFetching('postAlumnoApi');

  const debouncedRefresh = useDebouncedCallback(
    () => {alumnos.refetch();},
    1000,
  );

  useEffect(() => {
    debouncedRefresh();
  }, [qc]);

  useEffect(() => {
    if (deleteGrupo.data === true && !deleteGrupo.isLoading) {
      setTimeout(() => {
        alumnos.refetch();
        grupos.refetch();
      }, 500);
    }
  }, [deleteGrupo.dataUpdatedAt]);

  if (!user?._id) return null;

  const columns: GridColDef[] = [
    {
      field: 'nombre',
      headerName: 'Nombre',
      headerClassName: 'table-headers',
      flex: 1,
      maxWidth: 300,
      renderHeader: () => (<strong>Nombre</strong>),
      renderCell: (params) => {
        const grupo = params.row as GrupoType;
        const alumnosDelGrupo = alumnos.data?.filter((a) => a.grupo === grupo.nombre);
        return (
          <div>
            <Button
              fullWidth
              onClick={user?.roles?.includes('admin grupos') ? () => {
                setGrupoAEditar(grupo);
                setVerDetalleGrupo(true);
              } : undefined}
              variant="text"
              color="primary"
              style={{
                whiteSpace: 'pre-wrap',
                textAlign: 'left',
                fontWeight: 'bolder',
                fontSize: 18,
              }}
            >
              {params.row.nombre}
              {grupo.esEquipo && (
                <span style={{ marginLeft: 8, fontSize: 12, color: '#1976d2' }}>(equipo)</span>
              )}
            </Button>
            <br />
            <Button
              color="primary"
              variant='contained'
              size="small"
              fullWidth
              style={{
                marginTop: 8,
              }}
              onClick={() => setAgregarAlumnos({ alumnos: alumnos.data?.filter((a) => !a.grupo), grupo: grupo.nombre })}
            >
              Agregar Alumnos
            </Button>
            <br />
            <Button
              color="error"
              variant='contained'
              size="small"
              fullWidth
              onClick={() => {setGrupoABorrar(grupo._id); setWarning(true);}}
              style={{
                marginTop: 8,
              }}
            >
              Eliminar Grupo
            </Button>
            <Typography marginTop={2}>{`Cantidad de alumnos: ${alumnosDelGrupo?.length ?? 0}`}</Typography>
          </div>
        );
      },
    },
    {
      field: 'alumnos',
      headerName: 'Alumnos',
      headerClassName: 'table-headers',
      flex: 1,
      renderHeader: () => (<strong>Alumnos</strong>),
      renderCell: (params) => {
        const grupo = params.row as GrupoType;
        const alumnosDelGrupo = alumnos.data?.filter((a) => a.grupo === grupo.nombre).map((a) => (
          <Chip
            key={a._id}
            label={`${a.name} ${a.apellido}`}
            onDelete={() => {
              setMutableAlumno({ ...a, grupo: null });
              a.grupo = null;
              editAlumno.refetch();
            }}
            size="small"
            style={{ margin: 4 }}
          />
        ));
        return (
          <div style={{ textAlign: 'start' }}>
            {alumnosDelGrupo}
          </div>
        );
      },
    },
  ];

  if (grupos.isLoading || !grupos.data || alumnos.isLoading || !alumnos.data) return <LinearProgress />;
  const filtrado = grupos.data?.filter((g) => g.nombre?.toUpperCase().includes(filtro?.toUpperCase())) ?? [];

  return (
    <Box sx={{ height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 160px)', width: 1 }}>
      {(grupos.isFetching || alumnos.isFetching || editAlumno.isFetching) && <LinearProgress />}
      <AlumnosParaAgregarAlGrupo
        open={agregarAlumnos !== null}
        onCancell={() => setAgregarAlumnos(null)}
        agregar= {agregarAlumnos}
        setMutableAlumno={setMutableAlumno}
      />
      <WarningDialog
        open={warning}
        onAccept={() => {deleteGrupo.refetch(); setWarning(false);}}
        onCancell={() => setWarning(false)}
        texto="Esta por eliminar el grupo."
      />
      <GrupoDetail
        grupo={grupoAEditar}
        open={verDetalleGrupo}
        onClose={() => {
          setVerDetalleGrupo(false);
        }}
        refetchLista={()=>{
          grupos.refetch();
          alumnos.refetch();
        }}
      />
      <HeaderFab
        title='Agregar Grupo'
        onclick={()=>{
          setGrupoAEditar(undefined);
          setVerDetalleGrupo(true);
        }}
      />
      <Grid container>
        <Grid item xs={12} lg={8}>
          <TextField
            margin="dense"
            id="buscar"
            label="Buscar"
            type="text"
            fullWidth
            variant="standard"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFiltro(event.target.value);
            }}
            value={filtro}
            style={{ marginLeft: 8, marginRight: 8 }}
          />
        </Grid>
        <Grid item xs={12} lg={4} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <Typography fontSize={20} style={{ marginLeft: 8, marginBottom: 8 }}>
            {`Mostrando ${filtrado.length} ${filtrado.length === 1 ? 'grupo' : 'grupos'}`}
          </Typography>
        </Grid>
      </Grid>

      <DataGrid
        getRowHeight={() => 'auto'}
        rows={filtrado}
        columns={columns}
        getRowId={(row) =>  row._id}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        hideFooter
        disableSelectionOnClick
      />
    </Box>
  );
}
